import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MangaCard from "@/components/feature/MangaCard";
import JsonLd from "@/components/feature/JsonLd";
import { mangaList } from "@/mocks/manga";

export default function MangaDetail() {
  const { id } = useParams<{ id: string }>();
  const manga = mangaList.find((m) => m.id === id);
  const [tab, setTab] = useState<"chapter" | "intro">("chapter");
  const [liked, setLiked] = useState(false);

  const related = useMemo(
    () => manga ? mangaList.filter((m) => m.category === manga.category && m.id !== manga.id).slice(0, 4) : [],
    [manga],
  );

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="container-site text-center py-32">
          <p className="text-foreground-600">漫画不存在或已被移除</p>
          <Link to="/category" className="inline-block mt-5 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors">
            返回漫画分类
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={[
          {
            "@context": "https://schema.org",
            "@type": "ComicStory",
            name: manga.title,
            url: `https://mangains.github.io/manga/${manga.id}`,
            image: manga.cover,
            description: manga.description,
            author: manga.authors.map((a) => ({ "@type": "Person", name: a })),
            genre: manga.category,
            keywords: [...manga.tags, "漫画"].join(","),
            inLanguage: "zh-CN",
            datePublished: `${manga.year}-01-01`,
            dateModified: "2026-08-15",
            isPartOf: { "@type": "WebSite", name: "漫画网", url: "https://mangains.github.io/" },
            mainEntityOfPage: `https://mangains.github.io/manga/${manga.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://mangains.github.io/" },
              { "@type": "ListItem", position: 2, name: "漫画分类", item: "https://mangains.github.io/category" },
              { "@type": "ListItem", position: 3, name: `${manga.category}漫画`, item: `https://mangains.github.io/category?type=${encodeURIComponent(manga.category)}` },
              { "@type": "ListItem", position: 4, name: manga.title, item: `https://mangains.github.io/manga/${manga.id}` },
            ],
          },
        ]}
      />
      <Navbar />

      <main className="container-site pt-28 md:pt-32 pb-16">
        <nav aria-label="面包屑" className="flex items-center gap-2 text-sm text-foreground-600 mb-6 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <Link to="/category" className="hover:text-primary-600 transition-colors">漫画分类</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <Link to={`/category?type=${encodeURIComponent(manga.category)}`} className="hover:text-primary-600 transition-colors">
            {manga.category}漫画
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <span className="text-foreground-950 font-medium">{manga.title}</span>
        </nav>

        {/* 详情头部 */}
        <section className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-8 rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8">
          <div className="relative rounded-xl overflow-hidden aspect-[600/850] md:aspect-[280/400] w-full max-w-[280px] mx-auto md:mx-0">
            <img
              src={manga.cover}
              alt={`${manga.title}漫画封面`}
              title={`${manga.title}漫画 - 在线免费阅读`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">{manga.category}漫画</span>
              <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium">{manga.status}</span>
              {manga.tags.map((tag) => (
                <Link key={tag} to={`/category?keyword=${encodeURIComponent(tag)}`} className="px-2.5 py-1 rounded-md bg-background-200 text-foreground-700 text-xs hover:bg-background-300 transition-colors">
                  #{tag}
                </Link>
              ))}
            </div>

            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">{manga.title}</h1>

            <div className="mt-4 flex flex-wrap items-center gap-6 text-sm text-foreground-700">
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-500"></i>
                <strong className="text-foreground-950 text-lg">{manga.rating}</strong>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-primary-500"></i>
                {manga.views} 人气
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-heart-line text-primary-500"></i>
                {manga.likes.toLocaleString()} 收藏
              </span>
            </div>

            <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div className="flex gap-2">
                <dt className="text-foreground-600 shrink-0">作者</dt>
                <dd className="text-foreground-950">{manga.authors.join(" / ")}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-600 shrink-0">年份</dt>
                <dd className="text-foreground-950">{manga.year} 年</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-600 shrink-0">章节</dt>
                <dd className="text-foreground-950">{manga.chapters.length} 话</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-600 shrink-0">更新</dt>
                <dd className="text-foreground-950">{manga.latestChapter}</dd>
              </div>
            </dl>

            <p className="mt-5 text-sm text-foreground-700 leading-relaxed">{manga.description}</p>

            <div className="mt-7 flex flex-col sm:flex-row items-stretch gap-3">
              <Link
                to={`/reader/${manga.id}?chapter=${manga.chapters.length - 1}`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg"></i> 开始阅读
              </Link>
              <Link
                to={`/reader/${manga.id}?chapter=0`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-background-50 border border-background-300 text-foreground-950 font-semibold hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i> 从头阅读
              </Link>
              <button
                type="button"
                onClick={() => setLiked(!liked)}
                className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold transition-colors whitespace-nowrap ${
                  liked
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-50 border border-background-300 text-foreground-950 hover:bg-background-200"
                }`}
              >
                <i className={liked ? "ri-heart-fill" : "ri-heart-line"}></i>
                {liked ? "已收藏" : "收藏"}
              </button>
            </div>
          </div>
        </section>

        {/* 章节 / 简介 */}
        <section className="mt-10">
          <div className="flex items-center gap-2 mb-5">
            {[
              { key: "chapter", label: `章节列表 (${manga.chapters.length})` },
              { key: "intro", label: "漫画简介" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key as "chapter" | "intro")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  tab === t.key
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 border border-background-200 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "chapter" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {manga.chapters.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/reader/${manga.id}?chapter=${ch.id.replace(`${manga.id}-`, "") - 1}`}
                  className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 hover:border-primary-300 hover:bg-background-100 transition-colors"
                >
                  <span className="text-sm text-foreground-950 font-medium">{ch.title}</span>
                  <span className="text-xs text-foreground-500">{ch.pages} 页</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-xl text-foreground-950 mb-4">漫画简介</h2>
              <p className="text-foreground-700 leading-loose">{manga.description}</p>
              <p className="mt-4 text-foreground-700 leading-loose">
                《{manga.title}》是{manga.authors.join("、")}创作的{`${manga.category}`}漫画作品，连载于{manga.year}年，至今已更新至{manga.latestChapter}。
                漫画网提供高清漫画在线阅读服务，支持漫画翻页模式、漫画横屏阅读、漫画夜间模式，并可缓存下载离线观看，让看漫画更顺手。
              </p>
            </div>
          )}
        </section>

        {/* 相关推荐 */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-2xl text-foreground-950 mb-6">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}